import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { categories } from "@/types/tool";
import { LucideIcon } from "lucide-react";
import * as Icons from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface CategoryFilterProps {
  selectedCategory: string | null;
  onSelectCategory: (category: string | null) => void;
}

const CategoryFilter = ({ selectedCategory, onSelectCategory }: CategoryFilterProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0));
    setScrollLeft(scrollContainerRef.current?.scrollLeft || 0);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0);
    const walk = (x - startX) * 2;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const handleCategorySelect = (categoryValue: string | null) => {
    onSelectCategory(categoryValue?.toLowerCase() || null);
  };

  return (
    <ScrollArea className="w-full relative">
      <div
        ref={scrollContainerRef}
        className={`flex space-x-1.5 sm:space-x-2 p-1 cursor-${isDragging ? 'grabbing' : 'grab'} select-none`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <Button
          variant={selectedCategory === null ? "default" : "outline"}
          size="sm"
          onClick={() => handleCategorySelect(null)}
          className={`
            flex-shrink-0 transition-all duration-300 text-xs sm:text-sm px-2.5 sm:px-4
            ${selectedCategory === null
              ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg'
              : 'hover:bg-purple-50 border-purple-200 text-purple-700'
            }
          `}
        >
          All Professionals
        </Button>
        {categories.map((category) => {
          const IconComponent = Icons[category.icon as keyof typeof Icons] as LucideIcon;
          const isSelected = selectedCategory === category.name.toLowerCase();
          
          return (
            <Button
              key={category.name}
              variant={isSelected ? "default" : "outline"}
              size="sm"
              onClick={() => handleCategorySelect(category.name)}
              className={`
                flex-shrink-0 transition-all duration-300 gap-1.5 sm:gap-2 text-xs sm:text-sm px-2.5 sm:px-4
                ${isSelected
                  ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg'
                  : 'hover:bg-purple-50 border-purple-200 text-purple-700'
                }
              `}
            >
              <IconComponent className="h-3 w-3 sm:h-4 sm:w-4" />
              {category.name}
            </Button>
          );
        })}
      </div>
      <ScrollBar orientation="horizontal" className="hidden" />
    </ScrollArea>
  );
};

export default CategoryFilter;